/**
 * The only place in the app that calls fetch.
 *
 * It exists to hold two awkward truths about the API in one place instead of
 * scattering them through every screen:
 *
 *   1. The CSRF token is session-bound and changes underneath us. Logging in
 *      regenerates the session id, and completing a password reset destroys
 *      the session altogether. So a 403 csrf_failed is not necessarily a
 *      problem — it is often just a stale token, and the right response is to
 *      fetch a new one and try again, once.
 *
 *   2. A 401 can happen at any moment: the committee member's session expired,
 *      or someone reset their password on another device. Every caller would
 *      otherwise need to handle it. Instead the auth context registers a
 *      handler here and deals with it centrally.
 */

const BASE = '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly error: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the server told us why in a way worth showing a volunteer. */
  get isUserFacing(): boolean {
    return [409, 413, 422, 429].includes(this.status);
  }
}

let csrfToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

async function refreshCsrfToken(): Promise<string> {
  const response = await fetch(`${BASE}/auth/csrf`, { credentials: 'include' });
  if (!response.ok) {
    throw new ApiError(response.status, 'csrf_unavailable', 'Could not reach the server.');
  }
  const body = (await response.json()) as { csrf_token: string };
  csrfToken = body.csrf_token;
  return body.csrf_token;
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Internal: stops the csrf retry from looping. */
  retrying?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  // FormData sets its own Content-Type, including the multipart boundary.
  // Setting it here would produce a header with no boundary in it and a body
  // the server cannot parse.
  const isForm = options.body instanceof FormData;

  if (options.body !== undefined && !isForm) {
    headers['Content-Type'] = 'application/json';
  }
  if (UNSAFE.has(method)) {
    headers['X-CSRF-Token'] = csrfToken ?? (await refreshCsrfToken());
  }

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: options.body === undefined
      ? undefined
      : isForm
        ? (options.body as FormData)
        : JSON.stringify(options.body),
  });

  if (response.status === 204) {
    return undefined as T;
  }

  if (response.ok) {
    return (await response.json()) as T;
  }

  const failure = (await response.json().catch(() => ({}))) as {
    error?: string;
    detail?: string;
  };
  const error = failure.error ?? 'request_failed';

  // A stale token, most likely. Get a new one and try once more.
  if (response.status === 403 && error === 'csrf_failed' && !options.retrying) {
    await refreshCsrfToken();
    return request<T>(path, { ...options, retrying: true });
  }

  if (response.status === 401) {
    onUnauthorized?.();
  }

  throw new ApiError(
    response.status,
    error,
    failure.detail ?? defaultMessage(response.status),
  );
}

function defaultMessage(status: number): string {
  switch (status) {
    case 401:
      return 'Please sign in again.';
    case 403:
      return 'You do not have permission to do that.';
    case 404:
      return 'That record could not be found.';
    case 429:
      return 'Too many attempts. Please wait a little and try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

/** Drops empty strings and undefined, so the URL stays readable. */
export function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  const rendered = search.toString();
  return rendered === '' ? '' : `?${rendered}`;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  /** An upload. Same CSRF and retry handling as everything else. */
  postForm: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  refreshCsrfToken,
};
